/* eslint-disable react/no-multi-comp */
import React, { useState } from 'react';
import { useSynapse } from 'app/lib/synaptik';
import { find, capitalize } from 'lodash';
import { Flex, Icon, Switch, Text, Box, Collapse, styled } from '@goodrx/matisse';

import { NotificationChannels, NotificationStatuses } from 'app/config/constants';
import Settings from 'app/config/settings';

const { SMS, IN_APP } = NotificationChannels;
const { ENABLED, DISABLED, BLACKLISTED } = NotificationStatuses;

const topicFilter = (topic: any) => (notification: any) => notification.topic === topic;

interface AccordionIconProps {
  isOpen?: boolean;
}

const AccordionIcon = styled(Icon)<AccordionIconProps>(
  (p) => `
    transition: 175ms transform;
    transform: rotate(${p.isOpen ? 90 : 0}deg);
    color: ${p.theme.colors.greyDark};
  `
);

const StyledCollapseHeader = styled.div(
  (p) => `
    cursor: pointer;
    padding: 16px 0;
    color: ${p.theme.colors.greyDarkest};
    font-weight: 500;
    border-bottom: 1px solid ${p.theme.colors.greyLight};
  `
);

interface CollapseHeaderProps {
  title: any;
  onToggle: any;
  isOpen: any;
  enabledChannels: any;
}

const CollapseHeader = React.memo(({ title, onToggle, isOpen, enabledChannels }: CollapseHeaderProps) => (
  <StyledCollapseHeader onClick={onToggle}>
    <Flex data-qa={`${title} Header`}>
      <Box flex={1}>
        <Text color="greyDarkest">{title}</Text>
      </Box>
      <Box>
        <Text color="greyDark" fontWeight={300} mr={1} data-qa="channels">
          {enabledChannels}
        </Text>
      </Box>
      <AccordionIcon name="chevron-right" isOpen={isOpen} />
    </Flex>
  </StyledCollapseHeader>
));

const StyledSwitch = styled(Switch)((p) => [
  p.disabled &&
    `
      pointer-events: none;
    `,
]);

interface NotificationChannelProps {
  config: any;
  channel: any;
  status: any;
  onNotificationToggle: any;
}

const NotificationChannel = React.memo(
  ({ config, channel, status, onNotificationToggle }: NotificationChannelProps) => {
    const disabled = channel === SMS && status === BLACKLISTED;
    const color = disabled ? 'grey' : 'greyDarkest';

    const handleToggle = (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (!disabled) {
        onNotificationToggle({ channel, status });
      }
    };

    return (
      <Flex mb={3} flexDirection="column">
        <Flex alignItems="center">
          <Icon name={config.iconName} size={20} color={color} />
          <Flex ml={2}>
            <Text fontWeight={500} fontSize={14} color={color}>
              {config.displayName}
            </Text>
          </Flex>

          <Flex ml="auto">
            <div role="presentation" onClick={handleToggle} data-qa={`${config.displayName} Toggle`}>
              <StyledSwitch disabled={disabled} name="toggle" value={status === ENABLED} />
            </div>
          </Flex>
        </Flex>

        {disabled && (
          <Text mt={3} color="greyDark">
            Please text START to {Settings.PhoneNumbers.TRANSACTIONAL_ALERTS} to re-enable.
          </Text>
        )}
      </Flex>
    );
  }
);

const getNextStatus = (status: any) => (status === ENABLED ? DISABLED : ENABLED);

const ChannelDisplayNames = {
  [SMS]: 'Text',
  [IN_APP]: 'App',
};

interface Channel {
  topic: string;
  channel: string;
  status: string;
  config: {
    iconName: string;
    displayName: string;
    channel: string;
  };
}

interface TopicManagerProps {
  topic: string;
  channels: Channel[];
  topicDisplayName: string;
}

export const TopicManager = React.memo(({ topic, channels, topicDisplayName }: TopicManagerProps) => {
  const [patientNotifications, updateNotificationTopic] = useSynapse(({ user }) => [
    (user.state?.profile?.notifications || []).filter(topicFilter(topic)),
    user.updateNotificationTopic,
  ]);

  const [open, setOpen] = useState(false);

  const channelOptions = channels.map((config: any) => ({
    ...config,
    ...find(patientNotifications, { topic, channel: config.channel }),
  }));

  const handleToggle = ({ channel, status }: any) =>
    updateNotificationTopic({ channel, topic, status: getNextStatus(status) });

  const notificationChannels = channelOptions.map((channel: any) => (
    <NotificationChannel
      config={channel.config}
      key={channel.channel}
      status={channel.status || DISABLED}
      channel={channel.channel}
      onNotificationToggle={handleToggle}
    />
  ));

  const enabledChannels = channelOptions
    .map(({ channel, status }: any) => status === ENABLED && (ChannelDisplayNames[channel] || capitalize(channel)))
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <CollapseHeader
        isOpen={open}
        title={topicDisplayName}
        onToggle={() => setOpen(!open)}
        enabledChannels={enabledChannels || 'Off'}
      />

      <Collapse isOpen={open}>
        <Box pt={3}>{notificationChannels}</Box>
      </Collapse>
    </>
  );
});
